import { useState, useEffect } from 'react';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.querySelector('section');
      if (heroSection) {
        const heroHeight = heroSection.offsetHeight;
        setIsScrolled(window.scrollY > heroHeight - 100);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 72;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navItems = [
    { label: 'About', id: 'about' },
    { label: 'Books', id: 'books' },
    { label: 'Videos', id: 'videos' },
    { label: 'Contact', id: 'contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || mobileOpen ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">

            {/* Desktop: Left nav */}
            <div className="hidden md:flex items-center gap-8">
              {navItems.slice(0, 2).map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Mobile: Logo/Name on left */}
            <button
              onClick={scrollToTop}
              className="md:hidden flex items-center gap-2 cursor-pointer"
            >
              <img
                src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/2133e5c400fdc21ca6feb4502d01c8f1.jpeg"
                alt="Forrest Jamison"
                className="h-10 w-10 rounded-full object-cover"
              />
              <span className={`font-serif font-semibold text-base ${isScrolled || mobileOpen ? 'text-gray-900' : 'text-white'}`}>
                Forrest Jamison
              </span>
            </button>

            {/* Desktop: Center logo */}
            {isScrolled && (
              <button onClick={scrollToTop} className="hidden md:block cursor-pointer transition-all duration-300 absolute left-1/2 -translate-x-1/2">
                <img
                  src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/2133e5c400fdc21ca6feb4502d01c8f1.jpeg"
                  alt="Forrest Jamison"
                  className="h-14 w-14 rounded-full object-cover"
                />
              </button>
            )}

            {/* Desktop: Right nav */}
            <div className="hidden md:flex items-center gap-8">
              {navItems.slice(2).map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <div className="relative">
                <div className={`absolute -left-10 top-1/2 -translate-y-1/2 animate-bounce ${
                  isScrolled ? 'text-amber-600' : 'text-white'
                }`}>
                  <i className="ri-arrow-right-line text-2xl"></i>
                </div>
                <a
                  href="https://www.amazon.com/21-Days-Forrest-Jamison-ebook/dp/B0FW7N3R2Y"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`px-8 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap text-base ${
                    isScrolled
                      ? 'bg-amber-600 text-white hover:bg-amber-700'
                      : 'bg-white text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  Buy Book
                </a>
              </div>
            </div>

            {/* Mobile: Hamburger */}
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${isScrolled || mobileOpen ? 'text-gray-900' : 'text-white'} ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-6 pt-2">
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left py-3 px-2 text-gray-700 font-medium text-base border-b border-gray-50 hover:text-amber-600 transition-colors cursor-pointer"
                >
                  {item.label}
                </button>
              ))}
              <a
                href="https://www.amazon.com/21-Days-Forrest-Jamison-ebook/dp/B0FW7N3R2Y"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 w-full bg-amber-600 text-white py-3 rounded-lg font-medium text-center hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Buy Book on Amazon
              </a>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
